import type { SortDirection } from '@features/allocation';
import { allFiltersCleared, hasAnyFilter, SORT_DIRECTIONS, textOrNull } from '@features/allocation';
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { AllocationTagFilters, AllocationTagSortField } from '../types/AllocationTag';

const SORT_FIELDS = [
  'name',
  'color',
  'active',
  'allocationCount',
] as const satisfies readonly AllocationTagSortField[];

const PARSERS = {
  name: parseAsString,
  color: parseAsString,
  active: parseAsBoolean,
  sortBy: parseAsStringLiteral(SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(20),
};

export type AllocationTagSearchKey = 'name' | 'color';
export type AllocationTagFilterKey = AllocationTagSearchKey | 'active';

const CLEARED_BY_KEY = {
  name: { name: null },
  color: { color: null },
  active: { active: null },
} as const satisfies Record<AllocationTagFilterKey, object>;

const ALL_FILTERS_CLEARED = allFiltersCleared(CLEARED_BY_KEY);

/** Rascunho do painel "Filtros avançados", aplicado de uma vez. */
export type AllocationTagAdvancedValues = {
  name: string;
  color: string;
  active: boolean | null;
};

/** Filtros, ordenação e página na URL, no molde de `usePoolFilters`; filtro novo volta à página 1. */
export function useAllocationTagFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const filters: AllocationTagFilters = {
    name: state.name ?? undefined,
    color: state.color ?? undefined,
    active: state.active ?? undefined,
    sortBy: state.sortBy ?? undefined,
    sortDirection: state.sortBy ? (state.sortDirection ?? 'asc') : undefined,
    page: state.page,
    perPage: state.perPage !== 20 ? state.perPage : undefined,
  };

  return {
    filters,
    hasActiveFilters: hasAnyFilter(CLEARED_BY_KEY, state),
    setSearch: (key: AllocationTagSearchKey, value: string) =>
      setState({ [key]: textOrNull(value), page: 1 }),
    setActive: (active: boolean | null) => setState({ active, page: 1 }),
    applyAdvanced: (values: AllocationTagAdvancedValues) =>
      setState({
        name: textOrNull(values.name),
        color: textOrNull(values.color),
        active: values.active,
        page: 1,
      }),
    /** `'none'` volta à ordem do legado. */
    setSort: (field: AllocationTagSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    clearFilter: (key: AllocationTagFilterKey) => setState({ ...CLEARED_BY_KEY[key], page: 1 }),
    /** Limpa os filtros, não a ordenação. */
    clearFilters: () => setState({ ...ALL_FILTERS_CLEARED, page: 1 }),
    goToPage: (page: number) => setState({ page }),
    perPage: state.perPage,
    setPerPage: (perPage: number) => setState({ perPage, page: 1 }),
  };
}

export type AllocationTagFilterState = ReturnType<typeof useAllocationTagFilters>;
